
import {useState, useEffect} from "react"
import { ChevronDownIcon, CalendarDaysIcon } from "lucide-react"
import { cn } from "src/lib/utils"
import { TrashIcon } from "@heroicons/react/20/solid"

import { Button } from "./ui/button"
import { Calendar } from "./ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover"
import { format } from "date-fns"

export type DateFnsFormat =
  | "yyyy-MM-dd"
  | "dd/MM/yyyy"
  | "MM/dd/yyyy"
  | "dd MMM yyyy"
  | "dd MMMM yyyy"
  | "EEEE, MMMM do, yyyy"
  | "EEE, MMM d"
  | "MMMM yyyy"
  | "do MMM yyyy"
  | "yyyyMMdd"
  | "dd-MM-yyyy"

export interface DatePickerProps  {
  name: string;
  id? : string;
  label? : string;
  value? : Date | null
  onChange? : (value : Date|null) => void,
  required? : boolean ,
  formatStr? : DateFnsFormat | "MMM dd yyyy",
  errorMessage? : string,
  customClassName? : string,
  placeHolder? : string,
  showClearButton? : boolean
}

const DatePicker = ({ name, showClearButton, label, value,id, required, onChange, formatStr, errorMessage, customClassName, placeHolder } : DatePickerProps)  => {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState<Date | null>(value ?? null)
  const hasError = !!errorMessage;
  useEffect(() => {
    setDate(value ?? null)
  }, [value])

  return (
    <>
      {label && label.trim().length > 0 && (
        <label
          htmlFor={id || name}
          className="block text-sm font-semibold leading-6 text-gray-900"
        >
          {label}{required && <span className="text-red-500 font-bolder ml-1">*</span>}

        </label> 
      )}

      <div className={cn("flex", showClearButton ? "flex-3 gap-2" : "flex-1")}>
      <div className="flex-1">
        <Popover open={open} onOpenChange={setOpen} >
          <PopoverTrigger asChild>
            <Button
              name={name}
              variant="outline"
              id={id || name}
              className={cn( 
                  "justify-between font-normal",
                  (customClassName && customClassName.trim().length > 0) ? customClassName :
                  `text-sm mt-2 w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-inset focus:ring-2 sm:text-sm sm:leading-6`, 
                  !hasError ? ` ring-1 ring-gray-300 focus:ring-indigo-600 ` : `ring-2 ring-red-300 focus:ring-red-600 `)
              }
            >
              {date ? format(date, formatStr || "MMM d, yyyy") : (placeHolder || "Select date")}
              <CalendarDaysIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0 flex" align="start">
            <Calendar 
              mode="single"
              selected={date || undefined}
              captionLayout="dropdown"
              defaultMonth={date || undefined}
              onSelect={(date) => {
                setDate(date ?? null)
                setOpen(false)
                onChange?.(date || null)
              }}
            />          
          </PopoverContent>
        </Popover>
      
      </div>
      {showClearButton && (

      <div>
      {/* Clear Button */}
        <button
          type="button"
          className=" p-1 mt-2.5 text-gray-500 hover:text-gray-800"
          onClick={() => {setDate(null); onChange?.(null)}}
        >
          <span>
              <TrashIcon className="-ml-1 -mr-0.5 h-5 w-5 text-gray-600" aria-hidden="true" />
          </span>
        </button>
      </div>
      )}

      
      </div>

      {hasError && (
        <span className="text-red-600 text-sm">{errorMessage}</span>
      )}
    </>
  )
}

export default DatePicker;
